import React from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/input';

export type ProgressReportViewer = {
  id: number;
  realname: string;
  username: string;
};

type ProgressReportViewerPickerProps = {
  selected: ProgressReportViewer[];
  results: ProgressReportViewer[];
  search: string;
  onSearchChange: (value: string) => void;
  onAdd: (viewer: ProgressReportViewer) => void;
  onRemove: (viewerId: number) => void;
  disabled?: boolean;
};

export const ProgressReportViewerPicker = ({
  selected,
  results,
  search,
  onSearchChange,
  onAdd,
  onRemove,
  disabled = false,
}: ProgressReportViewerPickerProps) => {
  const selectedIds = new Set(selected.map((viewer) => viewer.id));

  return (
    <div className="mt-5 flex flex-col gap-2 border-t border-[var(--neutral-gray-40)] pt-4">
      <div>
        <div className="text-xs font-medium text-[var(--neutral-gray-100)]">Viewers</div>
        <div className="text-xs text-[var(--neutral-gray-80)]">Select users who will see this progress report.</div>
      </div>

      <div className="flex flex-wrap gap-2">
        {selected.map((viewer) => (
          <span key={viewer.id} className="inline-flex items-center gap-1 rounded-[var(--border-radius-medium)] bg-[var(--neutral-gray-20)] px-2 py-1 text-xs text-[var(--neutral-gray-120)]">
            {viewer.realname || viewer.username}
            {!disabled && (
              <button type="button" onClick={() => onRemove(viewer.id)} aria-label={`Remove ${viewer.realname || viewer.username}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ))}
        {selected.length === 0 && <span className="text-xs text-[var(--neutral-gray-80)]">No viewers selected.</span>}
      </div>

      {!disabled && <div className="relative max-w-xl">
        <Input value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Search users by name or username" />
        {search.trim().length >= 2 && (
          <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white shadow-[var(--shadow-medium)]">
            {results.filter((viewer) => !selectedIds.has(viewer.id)).map((viewer) => (
              <button
                key={viewer.id}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-[var(--neutral-gray-120)] hover:bg-[var(--neutral-gray-20)]"
                onClick={() => onAdd(viewer)}
              >
                <span className="font-medium">{viewer.realname || viewer.username}</span>
                <span className="ml-2 text-xs text-[var(--neutral-gray-80)]">{viewer.username}</span>
              </button>
            ))}
            {results.filter((viewer) => !selectedIds.has(viewer.id)).length === 0 && (
              <div className="px-3 py-2 text-xs text-[var(--neutral-gray-80)]">No matching users.</div>
            )}
          </div>
        )}
      </div>}
    </div>
  );
};
